import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';

import { CurrencySelector } from '@components/currency-and-date/currency/currency.component';
import { fxDisclosure } from '@components/currency-and-date/fx-disclosure';
import { LastUpdated } from '@components/currency-and-date/last-updated/last-updated.component';

import { FxDisclosureWire } from '@models/API/fx-disclosure.model';

import { CurrencyState } from '@services/state/currency-state/currency.state';

@Component({
  selector: 'currency-and-date',
  standalone: true,
  imports: [CurrencySelector, LastUpdated],
  template: `
    <div class="flex w-full justify-between">
      <div class="flex gap-4 [&_*]:text-muted">
        <last-updated />
        <currency-selector [tooltip]="tooltip()" />
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CurrencyAndDate {
  /**
   * The rate a chart on this page was converted with, when there is one.
   *
   * Optional because three things can leave it empty and none of them is an error: the
   * reading is native, the page has not loaded its charts yet, or the section has no
   * converted figures at all. In every one of those the disclosure still has something
   * true to say - the sources and the nature of the crossed pair - so the tooltip
   * degrades to what LEN-2089 delivered rather than to nothing.
   */
  appliedRate = input<FxDisclosureWire | undefined>(undefined);

  private currencyState = inject(CurrencyState);

  /**
   * An override, for a section that has to say something else. Kept from LEN-2089 and
   * still deliberately unused: overriding opts out of the single source, which is the
   * thing that subtask exists to prevent. It should be argued for, not assumed.
   */
  currencyTooltip = input<string | undefined>(undefined);

  /**
   * The override wins when a page insists; otherwise the shared text, folding in the
   * applied rate once the page has one. Recomputing rather than passing a string down
   * is what lets the factor appear when the charts arrive, without the page having to
   * remember to re-send anything.
   */
  tooltip = computed(
    () =>
      this.currencyTooltip() ??
      fxDisclosure(this.appliedRate() ?? this.currencyState.appliedRate())
  );
}
